/**
 * Theme Provider and Utilities
 * Manages theme state (light/dark/system), text size, and dynamic theme application.
 * When authenticated, theme and mode come from UIPreferences (GET /users/me/ui-preferences), not from platform themes/current.
 */

import { createContext, useContext, useEffect, useLayoutEffect, useState, useCallback, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import {
  applyThemeConfig,
  applyDefaultThemeTokens,
  resetDocumentAppearanceToDefaults,
  type DatabaseTheme,
} from '@/utils/theme-application.util'
import { useFontSize } from '@/hooks/useFontSize'
import { useUIPreferences } from '@/contexts/UIPreferencesContext'
import { ThemeEffectManager } from '@/components/theme/ThemeEffectManager'
import { isPublicRoute, usesPublicPlatformTheme } from '@/utils/route.util'
import { usePublicLandingTheme } from '@/hooks/usePublicLandingTheme'

export type ThemeMode = 'light' | 'dark' | 'system'
export type TextSize = 'sm' | 'md' | 'lg' | 'xl'

interface ThemeContextType {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  textSize: TextSize
  setTextSize: (size: TextSize) => void
  getEffectiveMode: () => 'light' | 'dark'
  databaseTheme: DatabaseTheme | null
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** Apply mode + theme to document in one synchronous pass (no rAF) to avoid flicker. */
function applyAppearanceSync(themeMode: ThemeMode, theme: DatabaseTheme | null): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const effectiveMode = themeMode === 'system' ? getSystemTheme() : themeMode
  if (effectiveMode === 'dark') root.classList.add('dark')
  else root.classList.remove('dark')
  if (theme) applyThemeConfig(theme, effectiveMode)
  else applyDefaultThemeTokens(effectiveMode)
}

function applyTextSizeSync(size: TextSize): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.remove('text-sm', 'text-md', 'text-lg', 'text-xl')
  root.classList.add(`text-${size}`)
  root.setAttribute('data-text-size', size)
}

interface ThemeProviderProps {
  children: ReactNode
  initialMode?: ThemeMode
  initialTextSize?: TextSize
}

/**
 * Theme Provider Component
 */
export function ThemeProvider({
  children,
  initialMode = 'light',
  initialTextSize = 'md',
}: ThemeProviderProps) {
  const location = useLocation()
  const appearanceFromUserSettings = !isPublicRoute(location.pathname)
  const publicPlatformTheme = usesPublicPlatformTheme(location.pathname)

  const { uiPreferences, updateUIPreferences, setUIPreferences } = useUIPreferences()
  const databaseTheme = (uiPreferences?.theme as DatabaseTheme | null) ?? null
  const preferredMode = (uiPreferences?.mode as ThemeMode) || 'light'

  const [mode, setModeState] = useState<ThemeMode>(() => preferredMode || initialMode)
  const [textSize, setTextSizeState] = useState<TextSize>(initialTextSize)

  // Apply font size from UI preferences (no platform font-sizes/current call when authenticated)
  useFontSize()

  // Marketing landing: apply GET /platform/themes/public light config (does not use user UI prefs).
  usePublicLandingTheme()

  // Sync text size from uiPreferences when available
  const preferredTextSize = (uiPreferences?.fontSize?.key as TextSize) || initialTextSize
  useEffect(() => {
    if (!appearanceFromUserSettings) return
    if (preferredTextSize && preferredTextSize !== textSize) {
      setTextSizeState(preferredTextSize)
    }
  }, [appearanceFromUserSettings, preferredTextSize, textSize])

  /** Get effective theme mode (resolves 'system' to actual light/dark). */
  const getEffectiveMode = useCallback((): 'light' | 'dark' => {
    return mode === 'system' ? getSystemTheme() : mode
  }, [mode])

  // Marketing + /onboarding: leave CSS vars to usePublicLandingTheme (light from ui-preferences or public API).
  // Other public shells (login): reset so user theme doesn't leak.
  // Authenticated app shell: apply mode + theme + text size before paint.
  useLayoutEffect(() => {
    if (!appearanceFromUserSettings) {
      if (!publicPlatformTheme) {
        resetDocumentAppearanceToDefaults()
      }
      return
    }
    applyAppearanceSync(mode, databaseTheme)
    applyTextSizeSync(textSize)
  }, [appearanceFromUserSettings, publicPlatformTheme, mode, databaseTheme, textSize])

  // Keep React theme state aligned with the document on public routes (hooks see light / defaults only).
  useEffect(() => {
    if (appearanceFromUserSettings) return
    setModeState('light')
    setTextSizeState('md')
  }, [appearanceFromUserSettings])

  // Sync local mode from server when uiPreferences.mode changes (e.g. after login or settings save)
  useEffect(() => {
    if (!appearanceFromUserSettings) return
    if (preferredMode && preferredMode !== mode) {
      setModeState(preferredMode)
    }
  }, [appearanceFromUserSettings, preferredMode, mode])

  /** Set theme mode: apply synchronously first so next paint has correct theme (no flicker). */
  const setMode = useCallback(
    (newMode: ThemeMode) => {
      if (appearanceFromUserSettings) applyAppearanceSync(newMode, databaseTheme)
      setModeState(newMode)
      if (typeof window !== 'undefined') {
        localStorage.setItem('vidhyantra-theme-mode', newMode)
      }
      updateUIPreferences({ mode: newMode }).then((updated) => {
        setUIPreferences(updated)
      }).catch(() => {})
    },
    [appearanceFromUserSettings, databaseTheme, updateUIPreferences, setUIPreferences]
  )

  /** Set text size: apply synchronously. */
  const setTextSize = useCallback((newSize: TextSize) => {
    applyTextSizeSync(newSize)
    setTextSizeState(newSize)
    if (typeof window !== 'undefined') {
      localStorage.setItem('vidhyantra-text-size', newSize)
    }
  }, [])

  // System preference listener: when mode is 'system', re-apply on OS theme change
  useEffect(() => {
    if (typeof window === 'undefined' || !appearanceFromUserSettings) return
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      if (mode === 'system') applyAppearanceSync('system', databaseTheme)
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [appearanceFromUserSettings, mode, databaseTheme])

  return (
    <ThemeContext.Provider
      value={{
        mode,
        setMode,
        textSize,
        setTextSize,
        getEffectiveMode,
        databaseTheme: (databaseTheme as DatabaseTheme | null) ?? null,
      }}
    >
      <ThemeEffectManager />
      {children}
    </ThemeContext.Provider>
  )
}

/**
 * Use theme hook
 */
export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

